'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import { ChevronRight, Minus, Plus } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '~/components/ui/dialog';
import { cn } from '~/lib/utils';

import { replaceAsteriskedTextWithFancyUnderline } from '../fancy-underline';
import { PreloadedClassyIframe } from '../preloaded-classy-iframe';
import truckStyles from './delivery-truck.module.css';
import s from './falling-gifts.module.css';
import kidStyles from './kid-profiles.module.css';
import { GIVE_HAPPINESS_KIDS, type GiveHappinessKid } from './kids';

type FallingGift = {
  id: number;
  name: string;
  image: string;
  style: Partial<
    React.CSSProperties & {
      '--translate-x': string;
      '--rotate-from': string;
      '--rotate-to': string;
    }
  >;
};

const FALLING_GIFTS_DATA: FallingGift[] = [
  {
    id: 1,
    name: 'Gift 1',
    image: '/img/give-happiness/gift-1.svg',
    style: {
      left: '10%',
      '--translate-x': '20px',
      '--rotate-from': '0deg',
      '--rotate-to': '360deg',
    },
  },
  {
    id: 2,
    name: 'Gift 2',
    image: '/img/give-happiness/gift-2.svg',
    style: {
      left: '60%',
      '--translate-x': '-15px',
      '--rotate-from': '45deg',
      '--rotate-to': '-270deg',
    },
  },
  {
    id: 3,
    name: 'Gift 3',
    image: '/img/give-happiness/gift-3.svg',
    style: {
      left: '85%',
      '--translate-x': '10px',
      '--rotate-from': '-30deg',
      '--rotate-to': '180deg',
    },
  },
];

function FallingGifts() {
  return (
    <div
      className={cn('pointer-events-none absolute inset-0', s.root)}
      style={
        {
          '--gift-count': FALLING_GIFTS_DATA.length,
        } as React.CSSProperties
      }
    >
      {FALLING_GIFTS_DATA.map((gift, index) => (
        <div
          key={gift.id}
          className={cn('absolute -z-20', s.gift)}
          style={{ '--index': index, ...gift.style } as React.CSSProperties}
        >
          <Image
            alt={gift.name}
            className='-z-10 object-contain object-center'
            draggable='false'
            fill
            src={gift.image}
          />
        </div>
      ))}
    </div>
  );
}

function DeliveryTruck() {
  return (
    <div className={cn(truckStyles.truck, 'lg:h-30 h-20 w-40 lg:w-60')}>
      <Image
        alt='Starlight Delivery Truck'
        src='/img/give-happiness/delivery-truck.svg'
        fill
        className='size-full object-contain'
        draggable='false'
      />
    </div>
  );
}

function useRunOnce(effect: () => void) {
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    effect();
  }, []);
}

export function GiveHappinessHero() {
  const [step, setStep] = useState(1);
  const [kidCount, setKidCount] = useState(3);
  const donationAmount = kidCount * 25; // $25 per kid

  const [kids, setKids] = useState<GiveHappinessKid[]>([]);

  useRunOnce(() => {
    const shuffledKids = [...GIVE_HAPPINESS_KIDS].sort(() => Math.random() - 0.5);
    setKids([...shuffledKids.slice(0, 5), { name: 'and more!', image: '/svg/and-more-star.svg' }]);
  });

  const incrementKids = () => setKidCount((prev) => prev + 1);
  const decrementKids = () => setKidCount((prev) => Math.max(prev - 1, 1));

  return (
    <div className='-mt-(--header-margin-bottom)'>
      <div className='lg:flex lg:flex-row'>
        {/* Left Side - Step Content */}
        <div className='bg-blue-light/15 flex flex-col items-center justify-center px-5 pb-12 pt-10 text-center lg:w-1/2 lg:shrink-0 lg:px-8 lg:py-14'>
          {step === 1 ? (
            <>
              <div className='mb-4 flex items-center lg:mb-6'>
                <Image src='/img/give-happiness/logo.png' alt='Give Happiness Logo' width={223.78} height={48} />
              </div>

              <h2 className='display-sm relative z-10 mx-auto max-w-lg text-balance'>
                {replaceAsteriskedTextWithFancyUnderline('How many kids can *you* help?', false)}
              </h2>

              <div className='mb-4 mt-6 flex items-center gap-x-8 lg:mb-4 lg:mt-8 '>
                <Button
                  className='bg-blue-light flex size-8 items-center justify-center rounded-full p-4 text-white md:size-10 xl:size-14 xl:p-3'
                  onClick={decrementKids}
                  disabled={kidCount <= 1}
                >
                  <Minus size={16} strokeWidth={6} className='shrink-0 text-white xl:size-5' />
                </Button>

                <div className='font-greycliff-extrabold text-blue min-w-32 text-8xl tracking-tight lg:min-w-48 lg:text-9xl xl:min-w-72 xl:text-[12rem] '>
                  {kidCount}
                </div>

                <Button
                  className='bg-blue-light flex size-8 items-center justify-center rounded-full p-4 text-white md:size-10 xl:size-14 xl:p-3'
                  onClick={incrementKids}
                >
                  {/* TODO: These icons arent the mock ups version */}
                  <Plus size={16} strokeWidth={6} className='shrink-0 text-white xl:size-5' />
                </Button>
              </div>

              <div className='flex w-full max-w-sm items-center justify-center rounded-full bg-white p-1 max-sm:w-full'>
                <p className='font-greycliff-extrabold min-w-41 flex h-full items-center rounded-l-full bg-white pl-6 pr-2'>
                  I will give ${donationAmount}
                </p>
                <Button className='grow' onClick={() => setStep(2)}>
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Step 2 - Classy Embed (preloaded iframe now visible) */}
              <div className='w-full'>{/* The preloaded iframe is now shown */}</div>
            </>
          )}

          {/* Single iframe that preloads in step 1 and shows in step 2 */}
          <PreloadedClassyIframe
            block={{ headline: 'Complete Your Donation', campaignId: '729262', recurringNudges: false }}
            amount={donationAmount}
            isVisible={step === 2}
            shouldPreload={step === 1}
          />
        </div>
        {/* Right Side - Kids Preview */}
        <div className='bg-blue-light/40 relative overflow-hidden px-5 pt-10 lg:w-1/2 lg:shrink-0 lg:px-8 lg:pt-16'>
          <FallingGifts />

          <div className='lg:pb-35 relative z-10 flex h-full flex-col pb-24'>
            {/* Header text */}
            <h2 className='heading-sm mb-6 text-center lg:mb-12'>You will send gifts to {kidCount} kids like:</h2>

            {/* Kids profiles */}
            <div className='shink-0 min-h-68 xl:min-h-100 mx-auto flex grow items-center justify-center max-md:max-w-sm lg:min-h-72'>
              <div
                className={cn(
                  'mx-auto flex w-full max-w-sm flex-wrap justify-center gap-4 lg:gap-x-12  lg:gap-y-8 xl:max-w-xl',
                  kidStyles.kidProfileContainer,
                )}
              >
                {kids.slice(0, Math.min(kidCount, 6)).map((kid, index) => {
                  const kidProfile = (
                    <div
                      className={cn(
                        'text-center transition-all',
                        kidStyles.kidProfile,
                        kid.bio && 'cursor-pointer hover:scale-105',
                      )}
                    >
                      <div
                        className={cn(
                          'mb-2 overflow-hidden md:mb-3',
                          kid.name === 'and more!' ? 'border-0' : 'xl:border-6 rounded-full border-4 border-white',
                        )}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={kid.image || '/placeholder.svg'}
                          alt={kid.name}
                          className={cn(
                            'aspect-square size-full',
                            kid.name === 'and more!' ? 'object-contain p-3' : 'object-cover',
                          )}
                        />
                      </div>
                      <p className='font-greycliff-extrabold xl:text-xl'>{kid.name}</p>
                    </div>
                  );

                  if (!kid.bio) {
                    return (
                      <div className='w-23 xl:w-36' key={`${kid.name}-${index}`}>
                        {kidProfile}
                      </div>
                    );
                  }

                  return (
                    <Dialog key={`${kid.name}-${index}`}>
                      <DialogTrigger asChild>
                        <button className='w-23 xl:w-36'>{kidProfile}</button>
                      </DialogTrigger>
                      <DialogContent className='lg:[&>button>svg]:text-foreground max-h-[90vh] gap-y-5 overflow-y-auto rounded-2xl p-6 sm:max-w-2xl lg:px-14 lg:py-10'>
                        <div className='flex items-center justify-between gap-4'>
                          <div>
                            <Image
                              src='/svg/shining-star-yellow.svg'
                              width={28.26}
                              height={32}
                              alt='Shining Star Icon'
                              className='mb-3 h-6 w-auto '
                            />

                            <DialogTitle className='font-greycliff-extrabold text-foreground text-2xl sm:text-4xl'>
                              Meet {kid.name}!
                            </DialogTitle>
                          </div>
                          <div className='size-20 shrink-0 overflow-hidden rounded-full sm:size-32'>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={kid.image} alt={kid.name} className='h-full w-full object-cover' />
                          </div>
                        </div>
                        <div className='body-lg space-y-4'>
                          {kid.bio?.split('\n').map((paragraph, i) => (
                            <p key={i}>{paragraph}</p>
                          ))}
                        </div>
                      </DialogContent>
                    </Dialog>
                  );
                })}
              </div>
            </div>
          </div>

          <DeliveryTruck />
        </div>
      </div>
    </div>
  );
}
